import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { filter } from "rxjs/operators";
import { AuthService } from "src/app/core/service/auth.service";
import { AulaTO, CreateLessonTO } from "src/app/data/schema/Courses/AulaTO";
import { CardPrecificacao } from "src/app/data/schema/Courses/CardPrecificacao";
import { CategoriaTO } from "src/app/data/schema/Courses/CategoriaTO";
import { CreateCourseTO } from "src/app/data/schema/Courses/CreateCourseTO";
import { CursoCardWelcomeTO } from "src/app/data/schema/Courses/CursoCardWelcomeTO";
import { CursoTO } from "src/app/data/schema/Courses/CursoTO";
import { DenunciaTO } from "src/app/data/schema/Courses/DenunciaTO";
import { QuestionFilter } from "src/app/data/schema/Courses/Enum/QuestionFilter";
import { QuestionOrder } from "src/app/data/schema/Courses/Enum/QuestionOrder";
import { CreateModuleTO, ModuloTO } from "src/app/data/schema/Courses/ModuloTO";
import { ModuloOrdenacaoTO } from "src/app/data/schema/Courses/OrdenacaoTO";
import { PatchCardCategoria } from "src/app/data/schema/Courses/PatchCardCategoria";
import { PatchCardTitulo } from "src/app/data/schema/Courses/PatchCardTitulo";
import { PerguntaTO } from "src/app/data/schema/Courses/PerguntaTO";
import { QuestionQueryParametersDTO } from "src/app/data/schema/Courses/QuestionQueryParametersDTO";
import { RequerimentoDTO } from "src/app/data/schema/Courses/RequerimentoDTO";
import { RespostaTO } from "src/app/data/schema/Courses/RespostaTO";
import { ReviewTO } from "src/app/data/schema/Courses/ReviewTO";
import { PublicQuestionTO } from "src/app/data/schema/Courses/public-question";
import { AchievementTO } from "src/app/data/schema/User/AchievementTO";
import { GrupoTO } from "src/app/data/schema/User/GrupoTO";
import { sha1 } from "src/app/utils/Sha1";
import { environment } from "src/environments/environment";
import { GetDataDTO } from "../../schema/Common/GetDataDTO";
import { AureaLessonDataDTO } from "../../schema/Courses/AureaLessonDataDTO";
import { CollaboratorDTO } from "../../schema/Courses/CollaboratorDTO";
import { CourseApresentationDTO } from "../../schema/Courses/CourseApresentationDTO";
import { CoursePackageDTO } from "../../schema/Courses/CoursePackageDTO";
import { CourseSummaryDTO } from "../../schema/Courses/CourseSummaryDTO";
import { CourseTypeDTO } from "../../schema/Courses/CourseTypeDTO";
import { CursoQueryParametersDTO } from "../../schema/Courses/CursoQueryParametersDTO";
import { PDFPlayerDTO } from "../../schema/Courses/Enum/PDFPlayerDTO";
import { SituacaoDTO } from "../../schema/Courses/Enum/SituacaoDTO";
import { ExternalMediaDataDTO } from "../../schema/Courses/ExternalMediaDataDTO";
import { KeyValueDTO } from "../../schema/Courses/KeyValueDTO";
import { OGDataDTO } from "../../schema/Courses/OGDataDTO";
import { PresentationTabDTO } from "../../schema/Courses/PresentationTabDTO";

@Injectable({
    providedIn: "root"
})
export class ApiCoursesService {
    constructor(private authService: AuthService, private http: HttpClient) {}

    clearCache(): void {
        const localStorageArray = Array.apply(0, new Array(localStorage.length)).map(function (o, i) {
            return localStorage.key(i);
        });

        const keys = localStorageArray.filter((x) => x.includes("CourseSummary_"));

        keys.forEach((key) => {
            localStorage.removeItem(key);
        });
    }

    getCoursesSummary(query: CursoQueryParametersDTO): Observable<GetDataDTO<CourseSummaryDTO>> {
        const behaviorSubject = new BehaviorSubject<GetDataDTO<CourseSummaryDTO>>(null);

        (async () => {
            const keyCache = "CourseSummary_" + sha1(JSON.stringify(query));
            const cacheValue = localStorage.getItem(keyCache);

            if (cacheValue != null) {
                behaviorSubject.next(JSON.parse(cacheValue));
            }

            this.getCoursesSummaryNoCache(query).subscribe((result) => {
                // Try catch colocado para evitar de local storage exceeded the quota
                try {
                    localStorage.setItem(keyCache, JSON.stringify(result));
                } catch {}
                behaviorSubject.next(result);
            });
        })();

        return behaviorSubject.pipe(filter((val) => val != null));
    }

    getCoursesSummaryNoCache(query: CursoQueryParametersDTO): Observable<GetDataDTO<CourseSummaryDTO>> {
        const skip = (query.pageNumber > 0 ? query.pageNumber - 1 : 0) * query.pageSize;
        let params: HttpParams = new HttpParams().set("skip", skip.toString()).set("take", query.pageSize.toString());

        if (query.filterCourseIds != null) {
            query.filterCourseIds.forEach((id) => (params = params.append("filterCourseIds", id.toString())));
        }

        if (query.subscribed == true || query.subscribed == false)
            params = params.set("subscribed", query.subscribed.toString());

        if (query.orderbyIes) params = params.set("orderbyIes", query.orderbyIes?.toString());

        if (query.titulo) params = params.set("titulo", query.titulo);

        if (query.filterCategory) params = params.set("filterCategory", query.filterCategory);

        if (query.filterUserGrupos) params = params.set("filterUserGrupos", query.filterUserGrupos.toString());

        if (query.onlyMyCourses != null) params = params.set("onlyMyCourses", query.onlyMyCourses.toString());

        if (query.filterSponsored != null) params = params.set("filterSponsored", query.filterSponsored.toString());
        if (query.filterFree != null) params = params.set("filterFree", query.filterFree.toString());

        if (query.situations?.length > 0)
            query.situations.forEach((v) => (params = params.append("situations", v.toString())));

        if (query.type?.length > 0) query.type.forEach((v) => (params = params.append("type", v.toString())));

        if (query.courseProgressionStatus?.length > 0)
            query.courseProgressionStatus.forEach(
                (v) => (params = params.append("courseProgressionStatus", v.toString()))
            );

        if (query.categories?.length > 0)
            query.categories.forEach((v) => (params = params.append("categories", v.toString())));

        if (query.groups?.length > 0) query.groups.forEach((v) => (params = params.append("groups", v.toString())));

        return this.http.get<GetDataDTO<CourseSummaryDTO>>(`${environment.courses.url}/api/v1/course`, {
            params: params
        });
    }

    getCourseApresentation(id: string): Observable<CourseApresentationDTO> {
        return this.http.get<CourseApresentationDTO>(`${environment.courses.url}/api/v1/course/${id}/apresentation`);
    }

    getCategorias(): Observable<CategoriaTO[]> {
        return this.http.get<CategoriaTO[]>(
            `${
                environment.courses.url
            }/api/v1/category?subscription=${this.authService.getCurrentSubscriptionId()}&isActive=true`
        );
    }

    getCoursesForRevision(): Observable<CourseSummaryDTO[]> {
        let params = "";
        params += "pageNumber=1&pageSize=100";

        return this.http.get<CourseSummaryDTO[]>(`${environment.courses.url}/api/v1/course/pendingReview?${params}`);
    }

    public publishCourse(courseId: string): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/Course/${courseId}/publish`, {});
    }

    public blockCourse(courseId: string, reason: string): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/Course/${courseId}/block?reason=${reason}`, {});
    }

    public getCoursesByTitle(
        searchTitle: string,
        pageNumber: number,
        pageSize: number,
        filterGroupsByLoggedUser: boolean
    ): Observable<GetDataDTO<CourseSummaryDTO>> {
        return this.getCoursesSummary({
            subscribed: false,
            titulo: searchTitle,
            pageSize: pageSize,
            pageNumber: pageNumber,
            orderbyIes: true,
            filterUserGrupos: filterGroupsByLoggedUser
        } as CursoQueryParametersDTO);
    }

    public ListQuestions(query: QuestionQueryParametersDTO): Observable<PerguntaTO[]> {
        let params = new HttpParams().set("skip", query.skip).set("take", query.take);

        if (query.filterBy) {
            params = params.set("filterBy", query.filterBy);
        }

        if (query.orderBy) {
            params = params.set("orderBy", query.orderBy);
        }

        if (query.CursoId) {
            params = params.set("CursoId", query.CursoId);
        }

        if (query.instructorId) {
            params = params.set("instructorId", query.instructorId);
        }

        if (query.find) {
            params = params.set("find", query.find);
        }

        if (query.fillCourse) {
            params = params.set("fillCourse", query.fillCourse);
        }

        return this.http.get<PerguntaTO[]>(`${environment.courses.url}/api/v1/Question`, { params });
    }

    public getCoursesByInstructorId(
        searchTitle: string,
        pageNumber: number,
        pageSize: number,
        onlyPublished?: boolean,
        categories?: string[],
        type?: CourseTypeDTO[],
        groups?: string[],
        situations?: SituacaoDTO[]
    ): Observable<GetDataDTO<CourseSummaryDTO>> {
        return this.getCoursesSummary({
            subscribed: false,
            titulo: searchTitle,
            pageSize: pageSize,
            pageNumber: pageNumber,
            orderbyIes: false,
            filterUserGrupos: false,
            onlyMyCourses: true,
            onlyPublished: onlyPublished,
            categories: categories,
            type: type,
            groups: groups,
            situations: situations
        } as CursoQueryParametersDTO);
    }

    getCourseById(id: string): Observable<CursoTO> {
        return this.http.get<CursoTO>(`${environment.courses.url}/api/v1/course/${id}`);
    }

    getQuestionById(id: string): Observable<PerguntaTO> {
        return this.http.get<PerguntaTO>(`${environment.courses.url}/api/v1/Question/${id}`);
    }

    getQuestionByIdCourse(cursoId: string, id: string): Observable<PerguntaTO> {
        return this.http.get<PerguntaTO>(`${environment.courses.url}/api/v1/Question/${id}/Course/${cursoId}`);
    }

    getCourseReviews(
        id: string,
        pageNumber: number,
        pageSize: number,
        orderBy: string,
        userId: string
    ): Observable<ReviewTO[]> {
        let params = new HttpParams()
            .set("skip", (pageNumber - 1).toString())
            .set("take", pageSize.toString())
            .set("orderBy", orderBy);

        if (userId) params = params.set("filterByUserId", userId);

        return this.http.get<ReviewTO[]>(`${environment.courses.url}/api/v1/Reviews/course/${id}`, { params: params });
    }

    createCourseReview(id: string, value: number, title: string, feedback: string): Observable<ReviewTO> {
        const review: ReviewTO = {
            idAutor: this.authService.getClaimsUserId(),
            nomeAutor: this.authService.getClaimsSub(),
            cursoId: id,
            quantidadeEstrelas: value,
            titulo: title,
            texto: feedback
        };
        return this.http.post<ReviewTO>(`${environment.courses.url}/api/v1/Reviews`, review);
    }

    getQuestionsByCourseId(
        courseId: string,
        filterBy: QuestionFilter = undefined,
        orderBy: QuestionOrder = undefined,
        pageNumber = 1,
        searchTitle: string = undefined
    ): Observable<PerguntaTO[]> {
        let params = "";
        if (params == "") {
            params = `pageNumber=${pageNumber}&pageSize=10`;
        }

        if (filterBy === QuestionFilter.NAO_RESPONDIDA) {
            params = params + `&filterBy=nao-respondida`;
        } else if (filterBy === QuestionFilter.RESPONDIDA) {
            params = params + "&filterBy=respondida";
        } else if (filterBy === QuestionFilter.FAVORITAS) {
            params = params + "&filterBy=favoritas";
        }

        if (orderBy === QuestionOrder.MAIS_ANTIGAS) {
            params = params + `&orderBy=mais-antigas`;
        } else if (orderBy === QuestionOrder.MAIS_CURTIDAS) {
            params = params + `&orderBy=mais-curtidas`;
        }

        if (searchTitle !== "" && searchTitle !== undefined && searchTitle !== null) {
            params = params + `&find=${searchTitle}`;
        }

        return this.http.get<PerguntaTO[]>(`${environment.courses.url}/api/v1/Question/Course/${courseId}?${params}`);
    }

    public getPublicQuestion(questionId: string, subscriptionId: string): Observable<PublicQuestionTO> {
        return this.http.get<PublicQuestionTO>(
            `${environment.courses.url}/api/v1/Question/${questionId}/public?subscriptionId=${subscriptionId}`
        );
    }

    public createQuestion(pergunta: PerguntaTO): Observable<PerguntaTO> {
        return this.http.post<PerguntaTO>(`${environment.courses.url}/api/v1/Question`, pergunta);
    }

    public uploadQuestionAttachment(questionId: string, file: FormData): Observable<PerguntaTO> {
        return this.http.post<any>(`${environment.courses.url}/api/v1/Question/${questionId}/attachment`, file);
    }

    public createAnswerQuestion(questionId: string, resposta: RespostaTO): Observable<RespostaTO> {
        return this.http.patch<RespostaTO>(`${environment.courses.url}/api/v1/Question/${questionId}/answer`, resposta);
    }

    public uploadQuestionAnswerAttachment(
        questionId: string,
        answerId: string,
        formData: FormData
    ): Observable<PerguntaTO> {
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/Question/${questionId}/answers/${answerId}/attachment`,
            formData
        );
    }

    public createDenunciationsQuestion(id: string, denuncia: DenunciaTO): Observable<DenunciaTO> {
        return this.http.patch<DenunciaTO>(`${environment.courses.url}/api/v1/question/${id}/denuncia`, denuncia);
    }

    public getCoursesByInstrutor = (archived = false): Observable<CourseSummaryDTO[]> => {
        return this.http.get<CourseSummaryDTO[]>(
            `${
                environment.courses.url
            }/api/v1/course/instructor/${this.authService.getClaimsUserId()}?archived=${archived}`
        );
    };

    public getAchievements = (): Observable<AchievementTO[]> => {
        return this.http.get<AchievementTO[]>(`${environment.courses.url}/api/v1/Achievements`);
    };

    public getOwnedCoursesQuestions = (
        pageNumber: number,
        filter: QuestionFilter,
        order: QuestionOrder,
        courseId: string,
        text: string
    ): Observable<PerguntaTO[]> => {
        const pageSize = 10;

        let params: HttpParams = new HttpParams()
            .set("pageNumber", (pageNumber - 1).toString())
            .set("pageSize", pageSize.toString())
            .set("filterBy", (filter || QuestionFilter.NONE).toString())
            .set("orderBy", (order || QuestionOrder.NONE).toString());

        if (courseId) {
            params = params.set("courseId", courseId);
        }

        if (text) {
            params = params.set("find", text);
        }

        return this.http.get<PerguntaTO[]>(
            `${environment.courses.url}/api/v1/question/instructor/${this.authService.getClaimsUserId()}`,
            { params: params }
        );
    };

    public createCourse(course: CreateCourseTO): Observable<CursoTO> {
        return this.http.post<CursoTO>(`${environment.courses.url}/api/v1/Course`, course);
    }

    public createModule(courseId: string, module: CreateModuleTO): Observable<ModuloTO> {
        return this.http.patch<ModuloTO>(`${environment.courses.url}/api/v1/Course/${courseId}/modulo`, module);
    }

    public updateModule(courseId: string, module: ModuloTO): Observable<ModuloTO> {
        return this.http.patch<ModuloTO>(
            `${environment.courses.url}/api/v1/Course/${courseId}/modulo/${module.id}`,
            module
        );
    }

    public deleteModule(courseId: string, moduleId: string): Observable<ModuloTO> {
        return this.http.delete<ModuloTO>(`${environment.courses.url}/api/v1/Course/${courseId}/modulo/${moduleId}`);
    }

    public createLesson(courseId: string, moduleId: string, newLesson: CreateLessonTO): Observable<AulaTO> {
        const data = {
            id: moduleId,
            aula: {
                titulo: newLesson.titulo,
                descricao: newLesson.descricao,
                habilitado: false,
                isOnlyExam: newLesson.isOnlyExam
            }
        };

        return this.http.patch<AulaTO>(
            `${environment.courses.url}/api/v1/Course/${courseId}/modulo/${moduleId}/aula`,
            data
        );
    }

    public updateLesson(courseId: string, moduleId: string, lesson: AulaTO): Observable<AulaTO> {
        const data = {
            id: moduleId,
            aula: lesson
        };

        return this.http.patch<AulaTO>(
            `${environment.courses.url}/api/v1/Course/${courseId}/modulo/${moduleId}/aula/${lesson.id}`,
            data
        );
    }

    public deleteLesson(courseId: string, lessonId: string): Observable<ModuloTO> {
        return this.http.delete<ModuloTO>(`${environment.courses.url}/api/v1/Course/${courseId}/aula/${lessonId}`);
    }

    public updateModulesAndLessonsOrdering(courseId: string, newOrderArray: ModuloOrdenacaoTO[]): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/ordenacaoModulos`,
            newOrderArray
        );
    }

    public updateCardTitle(courseId: string, data: PatchCardTitulo): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/Course/${courseId}/cardTitulo`, data);
    }

    public updateCardWelcome(courseId: string, data: CursoCardWelcomeTO): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/Course/${courseId}/cardWelcome`, data);
    }

    public updateCardPresentation(
        courseId: string,
        apresentacao: string,
        presentationTabs: PresentationTabDTO[]
    ): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/Course/${courseId}/cardApresentacao`, {
            apresentacao: apresentacao,
            presentationTabs: presentationTabs
        });
    }

    public addColaborator(courseId: string, collaborator: CollaboratorDTO): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/AddColaborator`,
            collaborator
        );
    }

    public addCourseToPackage(courseId: string, collaborator: CoursePackageDTO): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/AddCourseToPackage`,
            collaborator
        );
    }

    public removeColaborator(courseId: string, collaborator: CollaboratorDTO): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/RemoveCollaborator`,
            collaborator
        );
    }

    public removeCourseFromPackage(courseId: string, coursePackageId: string): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/RemoveCourseFromPackage/${coursePackageId}`,
            null
        );
    }

    public updateCardCategories(courseId: string, data: PatchCardCategoria): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/Course/${courseId}/cardCategoria`, data);
    }

    public updateCourseSkills(id: string, skills: string[]): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/Course/${id}/skills`, skills);
    }

    public updateCardGroups(courseId: string, data: GrupoTO[]): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/Course/${courseId}/updateGroups`, data);
    }

    public updateCardRequirements(courseId: string, data: RequerimentoDTO[]): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/Course/${courseId}/cardRequerimento`, {
            requerimentos: data
        });
    }

    updateIesCourses() {
        return this.http.post<any>(`${environment.courses.url}/api/v1/course/updateIesCourses`, null);
    }

    public postWelcomeVideo(id: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<any>(`${environment.courses.url}/api/v1/course/${id}/welcomeVideo`, formData, {
            reportProgress: true,
            observe: "events"
        });
    }

    postWelcomeVideoByGoogleDrive(id: string, driveUrl: string): Observable<any> {
        return this.http.post<any>(`${environment.courses.url}/api/v1/course/${id}/welcomeVideo/google-drive`, {
            url: driveUrl
        });
    }

    public reprocessWelcomeVideo(courseId: string): Observable<any> {
        return this.http.patch(`${environment.courses.url}/api/v1/course/${courseId}/welcomeVideo/reprocess`, null);
    }

    public postPresentationVideo(id: string, file?: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<any>(`${environment.courses.url}/api/v1/course/${id}/presentationVideo`, formData, {
            reportProgress: true,
            observe: "events"
        });
    }

    public postPresentationVideoFromLibrary(id: string, url: string, fileId: string): Observable<any> {
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/course/${id}/presentationVideo/library`,
            { url, fileId }
        );
    }

    postPresentationVideoByGoogleDrive(id: string, driveUrl: string): Observable<any> {
        return this.http.post<any>(`${environment.courses.url}/api/v1/course/${id}/presentationVideo/google-drive`, {
            url: driveUrl
        });
    }

    public reprocessPresentationVideo(courseId: string): Observable<any> {
        return this.http.patch(
            `${environment.courses.url}/api/v1/course/${courseId}/presentationVideo/reprocess`,
            null
        );
    }

    public patchPresentationVideo(courseId: string, videoUrl: string): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/course/${courseId}/presentationVideo`, {
            videoUrl
        });
    }

    public postWelcomeImage(id: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<any>(`${environment.courses.url}/api/v1/course/${id}/welcomeImage`, formData);
    }

    public postBanner(id: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<any>(`${environment.courses.url}/api/v1/course/${id}/banner`, formData);
    }

    public postLessonAttachments(courseId: string, moduleId: string, lessonId: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/course/${courseId}/${moduleId}/${lessonId}/attachments`,
            formData
        );
    }

    public updateLessonAttachment(
        courseId: string,
        moduleId: string,
        lessonId: string,
        fileName: string,
        readOnly: boolean
    ): Observable<any> {
        return this.http.patch(
            `${environment.courses.url}/api/v1/course/${courseId}/module/${moduleId}/lesson/${lessonId}/attachment`,
            { nome: fileName, readOnly: readOnly }
        );
    }

    public postLessonVideo(courseId: string, moduleId: string, lessonId: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/course/${courseId}/${moduleId}/${lessonId}/video`,
            formData,
            {
                reportProgress: true,
                observe: "events"
            }
        );
    }

    public postLessonVideoFromLibrary(courseId: string, moduleId: string, lessonId: string, url: string, fileId: string): Observable<any> {
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/course/${courseId}/module/${moduleId}/lesson/${lessonId}/video/library`,
            { url, fileId }
        );
    }

    public postLessonAttachmentsFromLibrary(courseId: string, moduleId: string, lessonId: string, files: { url: string; fileId: string }[]): Observable<any> {
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/course/${courseId}/${moduleId}/${lessonId}/library`,
             files
        );
    }

    postLessonVideoByGoogleDrive(
        courseId: string,
        moduleId: string,
        lessonId: string,
        driveUrl: string
    ): Observable<any> {
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/course/${courseId}/module/${moduleId}/lesson/${lessonId}/video/google-drive`,
            {
                url: driveUrl
            }
        );
    }

    public reprocessLessonVideo(courseId: string, moduleId: string, lessonId: string): Observable<any> {
        return this.http.patch(
            `${environment.courses.url}/api/v1/course/${courseId}/${moduleId}/${lessonId}/video/reprocess`,
            null
        );
    }

    public postQuestionAttachments(courseId: string, moduleId: string, lessonId: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/question/${courseId}/${moduleId}/${lessonId}/video`,
            formData
        );
    }

    public deleteLessonAttachment(courseId: string, moduleId: string, lessonId: string, fileName: string) {
        return this.http.delete<any>(
            `${environment.courses.url}/api/v1/course/${courseId}/${moduleId}/${lessonId}/attachments/${fileName}`
        );
    }

    public postLessonBanner(courseId: string, moduleId: string, lessonId: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/course/${courseId}/${moduleId}/${lessonId}/banner`,
            formData
        );
    }

    public UploadThumbnailApresentacao(courseId: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/course/${courseId}/ThumbnailApresentacao`,
            formData
        );
    }

    public requestReport(): Observable<any> {
        return this.http.post<any>(`${environment.courses.url}/api/v1/course/createReport`, {});
    }

    archiveCourse(courseId: string) {
        return this.http.patch(`${environment.courses.url}/api/v1/course/${courseId}/archive`, {});
    }

    unarchiveCourse(courseId: string) {
        return this.http.patch(`${environment.courses.url}/api/v1/course/${courseId}/unarchive`, {});
    }

    listSkills(): Observable<Array<string>> {
        return this.http.get<Array<string>>(`${environment.courses.url}/api/v1/Course/skills`);
    }

    // billing config
    public updateCoursePrecification(courseId: string, precification: CardPrecificacao): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/precification`,
            precification
        );
    }

    public updateCertificatePrecification(courseId: string, precification: CardPrecificacao): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/certificate/precification`,
            precification
        );
    }

    updateLessonExamResetProgression(
        courseId: string,
        moduleId: string,
        lessonId: string,
        examId: string,
        ExamResetProgression: boolean
    ): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/module/${moduleId}/lesson/${lessonId}/exam`,
            { examId, ExamResetProgression }
        );
    }

    public updateCardDescriptionOnCard(courseId: string, data: string): Observable<any> {
        return this.http.patch<any>(`${environment.courses.url}/api/v1/Course/${courseId}/cardDescrionOnCard`, {
            descriptionOnCard: data
        });
    }

    public UpdateParameters(courseId: string, parameters: KeyValueDTO[]): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/parameters`,
            JSON.stringify(parameters),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    public UpdateExternalMediaApresentation(courseId: string, externalMedia: ExternalMediaDataDTO): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/apresentation/externalMedia`,
            externalMedia
        );
    }

    public UpdateExternalMediaWelcome(courseId: string, externalMedia: ExternalMediaDataDTO): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/welcome/externalMedia`,
            externalMedia
        );
    }

    public UpdateExternalMediaLesson(
        courseId: string,
        moduleId: string,
        lessonId: string,
        externalMedia: ExternalMediaDataDTO
    ): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/module/${moduleId}/lesson/${lessonId}/externalMedia`,
            externalMedia
        );
    }

    public uploadScorm(courseId: string, file: File): Observable<any> {
        const formData = new FormData();
        formData.append("file", file);

        return this.http.post<any>(`${environment.courses.url}/api/v1/Course/${courseId}/scorm`, formData);
    }

    public uploadModuleScorm(courseId: string, moduleId: string, file: File): Observable<any> {
        const formData = new FormData();
        formData.append("file", file);

        return this.http.post<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/modules/${moduleId}/scorm`,
            formData
        );
    }

    public uploadLessonScorm(courseId: string, moduleId: string, lessonId: string, file: File): Observable<any> {
        const formData = new FormData();
        formData.append("file", file);

        return this.http.post<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/modules/${moduleId}/lesson/${lessonId}/scorm`,
            formData
        );
    }

    public UploadLessonPdf(courseId: string, moduleId: string, lessonId: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/module/${moduleId}/lesson/${lessonId}/pdf`,
            formData
        );
    }

    public UpdateLessonPdfPlayer(
        courseId: string,
        moduleId: string,
        lessonId: string,
        player: PDFPlayerDTO
    ): Observable<any> {
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/module/${moduleId}/lesson/${lessonId}/pdf-player`,
            { player }
        );
    }

    public UploadLessonImage(courseId: string, moduleId: string, lessonId: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
        return this.http.patch<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/module/${moduleId}/lesson/${lessonId}/image`,
            formData
        );
    }

    public saveOrderCoursesLearningPath(courseId: string, courses: CoursePackageDTO[]): Observable<any> {
        return this.http.patch(`${environment.courses.url}/api/v1/Course/${courseId}/package/courses`, courses);
    }

    public ClearContent(courseId: string, moduleId: string, lessonId: string): Observable<any> {
        return this.http.delete<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/module/${moduleId}/lesson/${lessonId}/content`
        );
    }

    public UpdatePublicVisibility(courseId: string, publicVisibility: boolean): Observable<any> {
        return this.http.patch(
            `${environment.courses.url}/api/v1/Course/${courseId}/publicVisibility`,
            publicVisibility.toString(),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    public RequestRecalculateProgressions(courseId: string): Observable<any> {
        return this.http.post<any>(
            `${environment.courses.url}/api/v1/Course/${courseId}/request-recalculate-progressions`,
            null
        );
    }

    public UpdateLessonIsOnlyExam(
        courseId: string,
        moduleId: string,
        lessonId: string,
        isOnlyExam: boolean
    ): Observable<unknown> {
        return this.http.patch<unknown>(
            `${environment.courses.url}/api/v1/Course/${courseId}/module/${moduleId}/lesson/${lessonId}/is-only-exam`,
            { IsOnlyExam: isOnlyExam }
        );
    }

    public UpdateOGData(courseId: string, data: OGDataDTO): Observable<unknown> {
        return this.http.post(`${environment.courses.url}/api/v1/Course/${courseId}/og-data`, data);
    }

    public GetAureaUrl(courseId: string, moduleId: string, lessonId: string): Observable<string> {
        return this.http.get(
            `${environment.courses.url}/api/v1/course/${courseId}/module/${moduleId}/lesson/${lessonId}/aurea`,
            { responseType: "text" }
        );
    }

    public UpdateAureaUrl(
        courseId: string,
        moduleId: string,
        lessonId: string,
        aureaLessonData: AureaLessonDataDTO
    ): Observable<unknown> {
        return this.http.patch<unknown>(
            `${environment.courses.url}/api/v1/course/${courseId}/module/${moduleId}/lesson/${lessonId}/aurea`,
            aureaLessonData
        );
    }
}
